import { Injectable } from '@angular/core';
import { Account } from '@mitel/cloudlink-sdk';
import { AppInjector } from './../../app-injector';
import { AccountsService } from './accounts.service';
import { AppUserClaimsService } from './app-user-claims.service';
import { SpinnerService } from './spinner.service';
import { AdminService } from './admin.service';
import { MenuItem } from '../interfaces/menu-item';
import { FlexConfigDialogComponent } from './../../modules/integrations-applications/components/integ-config-dialogs/flex-config-dialog/flex-config-dialog.component';
import { IntegrationItemServiceInf, StatusString } from './../interfaces/integrations';
import { UserRoles } from '../constants/constants';

@Injectable({
	providedIn: 'root'
})
export class IntegFlexService implements IntegrationItemServiceInf {

	private accountsService: AccountsService = undefined;
	private adminService: AdminService = undefined;
	private appClaimsService: AppUserClaimsService = undefined;
	private spinnerService: SpinnerService = undefined;

	constructor() { }

	initServices(): Promise<any> {

		if (!this.accountsService)
			this.accountsService = AppInjector.injector.get(AccountsService);

		if (!this.adminService)
			this.adminService = AppInjector.injector.get(AdminService);

		if (!this.appClaimsService)
			this.appClaimsService = AppInjector.injector.get(AppUserClaimsService);

		if (!this.spinnerService)
			this.spinnerService = AppInjector.injector.get(SpinnerService);

		return Promise.resolve();
	}

	getConfigurationDialog() {
		return FlexConfigDialogComponent;
	}

	doesAccountHaveOriaConfiguration(accountId: string): Promise<boolean> {
		return this.accountsService.getAccountTagsById(accountId).then((tags: any) => {
			console.log("The TAGS:", tags);
			if (tags && tags.flexConfiguration &&
				tags.flexConfiguration.webId &&
				tags.flexConfiguration.username &&
				tags.flexConfiguration.password) {
				return true;
			} else
				return false;
		});
	}

	updateFlexConfiguration(accountId, configuration) {
		const tagBody = { flexConfiguration: configuration };
		return this.adminService.createAccountTag(accountId, tagBody).then(tags => {
			console.log("The TAGS:", tags);
			this.accountsService.setAccountTag(tags);
		})
			.catch(err => {
				throw (err);
			})
	}

	isSetupComplete(account: Account): Promise<boolean> {
		// Here we will determine if a set up is required
		this.initServices();

		if (this.appClaimsService.isAssumedRole()) {
			//set up a free trial only if there is no MiCollab
			return this.doesAccountHaveOriaConfiguration(account.accountId).then(configurationExists => {
				if (configurationExists) {
					return true;
				}
				else {
					console.log("Oria Configuration are not present");
					return false;
				}
			});
		}
		else {
			console.log("set up complete: Not PARTNER role so no set up")
			return Promise.resolve(true);
		}
	}

	/*					
		An account will have this flexConfiguration tag if setup was completed.
		If the tag does not exist and we get a 404, that's ok - don't need to return error.
		A success here will have result = '' (no statusCode).
	*/
	handleIntegrationStateChange(account: Account, state: boolean, productsPriorChange?: string[]): Promise<any> {
		this.initServices();
		if (state == false) {
			console.log("Here we will delete Oria configuration from the account");
			return this.accountsService.deleteAccountTagById(account.accountId, 'flexConfiguration').then((result: any) => {
				if(result && result.statusCode){
					throw result; //get into the catch block to handle different statusCodes there
				}
				return;
			}).catch(err => {
				if(err && err.statusCode && err.statusCode === 404){
					console.log("Removing Oria configuration - setup was not completed");
					return;
				}
				console.error("There was an error deleting the flexConfiguration tag", err);
				throw err;
			});
		} else {
			return Promise.resolve(true);
		}
	}

	getLeftNavMenus(account: Account): Promise<MenuItem[]> {
		this.initServices();
		return this.isSetupComplete(account).then(complete => {
			if (!complete)
				return [];
			else {
				const phoneKeyTemplateItem = {
					name: 'PhoneKeyTemplate',
					translationKey: 'PhoneKeyTemplate',
					id: 'PhoneKeyTemplate',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/mivb/phoneKeyTemplate`,
					iconActive: '../../../../assets/images/account_active.svg',
					iconInactive: '../../../../assets/images/account_inactive.svg'						
				};
		
				const autoAttendantItem = {
					name: 'AutoAttendant',
					translationKey: 'AutoAttendant',
					id: 'AutoAttendant',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/mivb/autoAttendant`,
					iconActive: '../../../../assets/images/account_active.svg',
					iconInactive: '../../../../assets/images/account_inactive.svg'						
				};
		
				const callFlowsItem = {
					name: 'CallFlows',
					translationKey: 'CallFlows',
					id: 'CallFlows',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/mivb/callFlows`,
					iconActive: '../../../../assets/images/account_active.svg',
					iconInactive: '../../../../assets/images/account_inactive.svg'						
				};
		
				const callReroutingsItem = {
					name: 'CallReroutings',
					translationKey: 'CallReroutings',
					id: 'CallReroutings',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/mivb/callReroutings`,
					iconActive: '../../../../assets/images/account_active.svg',
					iconInactive: '../../../../assets/images/account_inactive.svg'						
				};
		
				const groupsItem = {
					name: 'Groups',
					translationKey: 'Groups',
					id: 'Groups',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/mivb/groups`,
					iconActive: '../../../../assets/images/account_active.svg',
					iconInactive: '../../../../assets/images/account_inactive.svg'						
				};
		
				const musicOnHoldItem = {
					name: 'MusicOnHold',
					translationKey: 'MusicOnHold',
					id: 'MusicOnHold',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/mivb/musicOnHold`,
					iconActive: '../../../../assets/images/account_active.svg',
					iconInactive: '../../../../assets/images/account_inactive.svg'						
				};
		
				const speedDialItem = {
					name: 'SpeedDial',
					translationKey: 'SpeedDial',
					id: 'SpeedDial',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/mivb/speedDial`,
					iconActive: '../../../../assets/images/account_active.svg',
					iconInactive: '../../../../assets/images/account_inactive.svg'						
				};
		
				const ACDItem = {
					name: 'ACD',
					translationKey: 'ACD',
					id: 'ACD',
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: `/accounts/${account.accountId}/mivb/acd`,
					iconActive: '../../../../assets/images/account_active.svg',
					iconInactive: '../../../../assets/images/account_inactive.svg'						
				};

				const miVBManagementItem = {
					name: 'MiVBManagement',
					translationKey: 'MiVBManagement',
					id: "MiVBManagement",
					minRole: UserRoles.ACCOUNT_ADMIN,
					navigation: "/norouting",
					iconActive: '../../../../assets/images/users_active.svg',
					iconInactive: '../../../../assets/images/users_inactive.svg',
					children: [
						phoneKeyTemplateItem,
						autoAttendantItem,
						callFlowsItem,
						callReroutingsItem,
						groupsItem,
						musicOnHoldItem,
						speedDialItem,
						ACDItem
					]
				};
				console.log("return left nav menus for MiVB integration", [miVBManagementItem]);
				return Promise.resolve( [miVBManagementItem] );
			}
		});
	}

	canBeSelected(account: Account): Promise<boolean>{
		// not allowing this integration to be added right now
		// this might come into play when we progress with the MiVB hybrid apps
		return Promise.resolve(false);
	}

	canDeleteIntegration(account: Account): Promise<boolean> {
		return Promise.resolve(true);
	}

	getConfigurationPageUrl(account: Account): string {
		return undefined;
	}

	getStatusString(account: Account): Promise<StatusString> {
		return undefined;
	}

	getConfigButtonLabel(account: Account): Promise<string> {
		return undefined;
	}

	isButtonDisabled(account: Account): Promise<boolean> {
		return Promise.resolve(false);
	}

}
